import { Image } from 'expo-image'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React from 'react'
import { Pressable, Text, View } from 'react-native'
import { tv } from 'tailwind-variants'

import airpodsImage from '@/assets/success/airpods.png'
import { stepContent } from '@/components/flowStep/styles'
import { useLocaleParts } from '@/hooks/useLocaleParts'
import { resolveImageSrc } from '@/utils/imageSrc'
import InfoButton from '@ui-kit/components/InfoButton'
import PhosphorIcon from '@ui-kit/components/PhosphorIcon'
import { COLOR } from '@ui-kit/constants'
import { body1, body2, caption } from '@ui-kit/core/typography'

const AIRPODS_IMAGE_URL =
  'https://cdn.sanity.io/images/p4gom3ch/production/e627c2a52cd4449a01c58a0a4e778efa56a72953-495x320.png?h=140&auto=format'

export const getAirpodsTermsPath = (language: string) =>
  `/documents/airpods_dental_cleaning_terms_and_conditions.pdf?lang=${language}`

const CLAIM_STEPS = ['step1', 'step2', 'step3', 'step4'] as const

const airpodsStyles = tv({
  slots: {
    topBar: 'w-full flex-row items-center justify-center gap-2 bg-[#AEF4FF] px-4 py-2',
    topBarText: caption({ color: 'black', className: 'text-center' }),
    couponSection: 'mt-4 gap-4',
    codeChip:
      'flex-row items-center justify-between rounded-lg border border-grey-200 bg-white px-3 py-3',
    codeChipCode: body2({ color: 'black', bold: true }),
    codeChipStatus: 'flex-row items-center gap-1',
    codeChipStatusText: body2({ color: 'success' }),
    perk: 'flex-row items-start gap-2',
    perkImage: '-ml-3 h-[70px] w-[71px] shrink-0',
    perkText: 'flex-1 gap-0.5',
    perkTitle: body1({ color: 'black', bold: true, className: 'whitespace-nowrap' }),
    perkCondition: body2({ color: 'grey600', bold: true }),
    claimPanel:
      'w-full overflow-hidden rounded-3xl border border-grey-200 bg-grey-100 lg:rounded-[40px] lg:border-0 lg:bg-grey-200 lg:px-12 lg:py-10',
    claimMedia: 'h-[190px] items-center justify-center bg-grey-200 lg:hidden',
    claimBody: 'gap-4 px-5 pb-6 pt-5 lg:gap-6 lg:p-0',
    claimTitle:
      'font-inter-semibold text-[20px] leading-6 text-black lg:text-[24px] lg:leading-[29px]',
    claimRow: 'flex-row flex-wrap items-center gap-5',
    claimStepsColumn: 'shrink grow basis-[240px]',
    claimSteps: 'gap-3',
    claimStep: 'flex-row items-center gap-2',
    claimStepNumber: 'h-[18px] w-[18px] items-center justify-center rounded-full bg-black',
    claimStepNumberText: 'font-inter-semibold text-[9px] leading-[13px] text-white',
    claimStepText: caption({ color: 'black', bold: true, className: 'flex-1 leading-[18px]' }),
    claimTermsLink: 'mt-4 lg:mt-8',
    claimTerms: caption({
      color: 'grey600',
      className: 'underline lg:text-black lg:opacity-70',
    }),
    claimImageFrame: 'hidden h-[188px] w-[191px] shrink-0 items-center justify-center lg:flex',
    claimImage: 'h-[167px] w-[135px]',
  },
  variants: {
    newDesign: {
      true: {
        codeChip: 'border-[#ddd] py-[15px] pl-3.5 pr-3',
        codeChipCode: 'leading-[21px]',
        codeChipStatusText: body2({ color: 'uiGreen', bold: true, className: 'leading-[21px]' }),
        perk: 'items-center',
      },
    },
  },
})

export const AirpodsTopBar = ({ productName }: { productName: string }) => {
  const { t } = useTranslation()
  const styles = airpodsStyles()

  return (
    <View className={styles.topBar()} testID="airpods-top-bar">
      <PhosphorIcon name="Gift" size={16} />
      <Text className={styles.topBarText()}>
        {t('sharedFlows_common:campaigns.airpods.topBar', { productName })}
      </Text>
    </View>
  )
}

export const AppliedCouponCode = ({ code, newDesign }: { code: string; newDesign?: boolean }) => {
  const { t } = useTranslation()
  const styles = airpodsStyles({ newDesign })

  return (
    <View className={styles.codeChip()}>
      <Text className={styles.codeChipCode()}>{code}</Text>
      <View className={styles.codeChipStatus()}>
        <PhosphorIcon
          name="CheckCircle"
          size={16}
          weight="fill"
          color={newDesign ? COLOR.uiGreen : COLOR.success}
        />
        <Text className={styles.codeChipStatusText()}>
          {t('sharedFlows_common:campaigns.airpods.codeApplied')}
        </Text>
      </View>
    </View>
  )
}

export const AirpodsCouponSection = ({
  code,
  className,
  newDesign,
}: {
  code: string
  className?: string
  newDesign?: boolean
}) => {
  const { t } = useTranslation()
  const styles = airpodsStyles({ newDesign })

  return (
    <View className={styles.couponSection({ className })}>
      <AppliedCouponCode code={code} newDesign={newDesign} />
      <View className={styles.perk()} testID="airpods-perk">
        <Image
          source={AIRPODS_IMAGE_URL}
          alt="Apple AirPods 4"
          className={styles.perkImage()}
          contentFit="cover"
        />
        <View className={styles.perkText()}>
          <Text className={styles.perkTitle()}>
            {t('sharedFlows_common:campaigns.airpods.included')}
          </Text>
          <Text className={styles.perkCondition()}>
            {t('sharedFlows_common:campaigns.airpods.includedCondition')}
          </Text>
        </View>
        <InfoButton
          type="info"
          modalTitle={t('sharedFlows_common:campaigns.airpods.info.title')}
          modalContent={t('sharedFlows_common:campaigns.airpods.info.text')}
        />
      </View>
    </View>
  )
}

export const AirpodsClaimSteps = () => {
  const { t } = useTranslation()
  const { language } = useLocaleParts()
  const styles = airpodsStyles()

  return (
    <View className={styles.claimPanel()} testID="airpods-claim-steps">
      <View className={styles.claimMedia()}>
        <Image
          source={resolveImageSrc(airpodsImage)}
          alt="Apple AirPods 4"
          className={styles.claimImage()}
          contentFit="contain"
        />
      </View>
      <View className={styles.claimBody()}>
        <Text className={styles.claimTitle()}>
          {t('sharedFlows_common:campaigns.airpods.success.title')}
        </Text>
        <View className={styles.claimRow()}>
          <View className={styles.claimStepsColumn()}>
            <View className={styles.claimSteps()}>
              {CLAIM_STEPS.map((step, index) => (
                <View key={step} className={styles.claimStep()}>
                  <View className={styles.claimStepNumber()}>
                    <Text className={styles.claimStepNumberText()}>{index + 1}</Text>
                  </View>
                  <Text className={styles.claimStepText()}>
                    {t(`sharedFlows_common:campaigns.airpods.success.${step}`)}
                  </Text>
                </View>
              ))}
            </View>
            <View className={styles.claimTermsLink()}>
              <Link href={getAirpodsTermsPath(language)} target="_blank" rel="noopener noreferrer">
                <Text className={styles.claimTerms()}>
                  {t('sharedFlows_common:campaigns.airpods.success.terms')}
                </Text>
              </Link>
            </View>
          </View>
          <View className={styles.claimImageFrame()}>
            <Image
              source={resolveImageSrc(airpodsImage)}
              alt="Apple AirPods 4"
              className={styles.claimImage()}
              contentFit="contain"
            />
          </View>
        </View>
      </View>
    </View>
  )
}

export const AirpodsNotEligibleModal = ({ onContinue }: { onContinue: () => void }) => {
  const { t } = useTranslation()
  const {
    otpOverlay,
    otpCard,
    otpHeader,
    otpTitle,
    otpDescription,
    otpConfirmButton,
    otpConfirmText,
  } = stepContent()

  return (
    <View className={otpOverlay()} testID="airpods-not-eligible-modal">
      <View className={otpCard()}>
        <View className={otpHeader()}>
          <Text className={otpTitle()}>
            {t('sharedFlows_common:campaigns.airpods.notEligible.title')}
          </Text>
        </View>
        <Text className={otpDescription()}>
          {t('sharedFlows_common:campaigns.airpods.notEligible.text')}
        </Text>
        <Pressable className={otpConfirmButton()} onPress={onContinue}>
          <Text className={otpConfirmText()}>
            {t('sharedFlows_common:campaigns.airpods.notEligible.continue')}
          </Text>
        </Pressable>
      </View>
    </View>
  )
}
